// src/components/MaintenanceContractWizard.js

import React, { useState, useEffect, useRef } from "react";
import { Stepper, Step, StepLabel } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import FormTemplate from "./AddMaintenanceContractWizard-Form1"; // Adjust the import path accordingly
import API_BASE_URL from "../../../../Constants";
import NavHeightAdjust from "../../../../components/navbar/NavHeightAdjust";

// Step titles
const steps = [
  "Contract Details",
  "Coverage",
  "Billing Info",
  "Increase Details",
];

// Step fields configuration
const stepFields = [
  // Step 1: Contract Details
  [
    { name: "contractNumber", label: "Contract Number", type: "text" },
    {
      name: "contractClass",
      label: "Contract Class",
      type: "select",
      fetchUrl: API_BASE_URL + "/api/maintenance/contractclasses",
      optionLabel: "contractClass",
      optionValue: "contractClass",
    },
    { name: "coverageStartDate", label: "Coverage Date", type: "date" },
  ],
  // Step 2: Coverage
  [
    {
      name: "coverageType",
      label: "Coverage Type",
      type: "select",
      fetchUrl: API_BASE_URL + "/api/maintenance/maintenancecoveragetypes",
      optionLabel: "coverageTypeName",
      optionValue: "coverageTypeName",
    },
    {
      name: "coverage",
      label: "Coverage",
      type: "select",
      fetchUrl: API_BASE_URL + "/api/maintenance/maintenancecoverages",
      optionLabel: "coverageName",
      optionValue: "coverageName",
    },
    {
      name: "visitFrequency",
      label: "Visit Frequency",
      type: "select",
      fetchUrl: API_BASE_URL + "/api/maintenance/maintenancefrequencies",
      optionLabel: "visitFrequencyName",
      optionValue: "visitFrequencyName",
    },
  ],
  // Step 3: Billing Info
  [
    {
      name: "billingLevel",
      label: "Billing Level",
      type: "select",
      fetchUrl: API_BASE_URL + "/api/maintenance/maintenancebillinglevels",
      optionLabel: "billingLevelName",
      optionValue: "billingLevelName",
    },

    {
      name: "billingFrequency",
      label: "Billing Frequency",
      type: "select",
      fetchUrl: API_BASE_URL + "/api/maintenance/maintenancebillingfrequencies",
      optionLabel: "billingFrequencyName",
      optionValue: "billingFrequencyName",
    },
    { name: "invoiceAmount", label: "Invoice Amount", type: "text" },
  ],
  // Step 4: Increase Details
  [
    { name: "increaseDate", label: "Increase Date", type: "date" },
    { name: "increasePercent", label: "Increase Percent", type: "text" },
    {
      name: "holdPeriod",
      label: "Hold Period",
      type: "select",
      fetchUrl: API_BASE_URL + "/api/maintenance/maintenanceholdperiods",
      optionLabel: "holdPeriod",
      optionValue: "holdPeriod",
    },
  ],
];

const MaintenanceContractWizard = () => {
  // State to manage the active step
  const [activeStep, setActiveStep] = useState(0);

  // State to manage form data
  const [formData, setFormData] = useState({
    contractNumber: "",
    contractClass: "",
    coverageStartDate: "",
    coverageType: "",
    coverage: "",
    visitFrequency: "",
    billingLevel: "",
    billingFrequency: "",
    invoiceAmount: "",
    increaseDate: "",
    increasePercent: "",
    increaseFrequency: "",
    holdPeriod: "",
    buildingId: "",
    contractStatus: "Active",
  });

  const navigate = useNavigate();
  const location = useLocation();

  const formRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    if (location.state && location.state.buildingId) {
      setFormData((prev) => ({
        ...prev,
        buildingId: location.state.buildingId,
      }));
    }
  }, [location.state]);

  // Adjusting height for form and timeline synchronization
  useEffect(() => {
    if (formRef.current && timelineRef.current) {
      const maxHeight = Math.max(
        formRef.current.offsetHeight,
        timelineRef.current.offsetHeight
      );
      formRef.current.style.height = `${maxHeight}px`;
      timelineRef.current.style.height = `${maxHeight}px`;
    }
  }, [activeStep]);

  // Handle moving to the next step
  const handleNext = () => setActiveStep((prevStep) => prevStep + 1);

  // Handle moving back to the previous step
  const handleBack = () => setActiveStep((prevStep) => prevStep - 1);

  // Handle changing form field values
  const handleChange = (name, value) =>
    setFormData({ ...formData, [name]: value });

  // Handle click on a specific step in the Stepper
  const handleStepClick = (index) => setActiveStep(index);

  // Handle finishing the form and submitting data
  const handleFinish = async () => {
    try {
      const response = await fetch(
        API_BASE_URL + "/api/maintenance/maintenance-contracts",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok)
        throw new Error("Failed to create maintenance contract");

      alert("Maintenance contract created successfully");
      navigate(-1); // Navigate back to the previous page
    } catch (error) {
      console.error("Error creating maintenance contract:", error);
      alert("Failed to create maintenance contract");
    }
  };

  // Handle cancel action
  const handleCancel = () => {
    navigate(-1); // Navigate back to the previous page
  };

  return (
    <NavHeightAdjust>
      <div className="flex flex-col lg:flex-row items-start p-5 lg:p-10 gap-6 max-w-5xl mx-auto">
        <div
          ref={formRef}
          className="w-full lg:w-3/4 p-6 bg-white rounded-lg border border-gray-300 shadow-xl flex flex-col mx-auto"
        >
          <h2 className="text-xl lg:text-2xl mb-4 mt-10 font-bold text-center">
            Maintenance Contract Wizard
          </h2>
          <div className="flex-grow p-4">
            <FormTemplate
              fields={stepFields[activeStep]}
              formData={formData}
              onChange={handleChange}
            />
          </div>
          <div className="mt-auto flex items-center justify-between pt-4">
            <span className="text-gray-600 font-semibold">
              Page {activeStep + 1} of {steps.length}
            </span>
            <div className="flex space-x-4">
              {activeStep === 0 && (
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 rounded-md border border-gray-400 transition-transform transform hover:scale-105 text-white bg-red-500 hover:bg-red-600"
                >
                  <h4 className="font-semibold hover:font-extrabold">Cancel</h4>
                </button>
              )}
              {activeStep > 0 && (
                <button
                  onClick={handleBack}
                  className="px-4 py-2 rounded-md border border-gray-400 transition-transform transform hover:scale-105 text-white bg-gray-300 hover:bg-sss-blue-sel"
                >
                  <h4 className="font-semibold hover:font-extrabold">Back</h4>
                </button>
              )}
              <button
                onClick={
                  activeStep === steps.length - 1 ? handleFinish : handleNext
                }
                className="px-4 py-2 rounded-md border border-gray-400 transition-transform transform hover:scale-105 text-white bg-sss-blue-sel hover:bg-blue-600"
              >
                <h4 className="font-semibold hover:font-extrabold">
                  {activeStep === steps.length - 1 ? "Finish" : "Next"}
                </h4>
              </button>
            </div>
          </div>
        </div>

        <div
          ref={timelineRef}
          className="w-full lg:w-2/5 p-5 lg:p-10 bg-white rounded-lg border border-gray-300 shadow-xl flex flex-col"
        >
          <Stepper
            activeStep={activeStep}
            orientation="vertical"
            className="gap-3 flex-grow"
          >
            {steps.map((label, index) => (
              <Step
                key={index}
                onClick={() => handleStepClick(index)}
                className={`transition-transform transform hover:scale-105 hover:shadow-lg rounded-md cursor-pointer ${
                  activeStep === index
                    ? "bg-sss-blue-sel border-2 border-gray-900  p-2"
                    : "bg-sss-blue-sel opacity-80 border border-gray-300 p-2"
                }`}
              >
                <StepLabel className="p-4">
                  <h4 className="text-white">{label}</h4>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>
      </div>
    </NavHeightAdjust>
  );
};

export default MaintenanceContractWizard;
